import { useTopicStore } from '../features/detailsCardV2/store';
import { iconWorkerManager } from '../utils/iconWorkerManager';
import { wikiWorkerManager } from '../utils/wikiWorkerManager';
import { patchAppState, setAppField, useAppStore } from './store';

const FALLBACK_THUMBNAIL = 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4a/Commons-logo.svg/80px-Commons-logo.svg.png';
const TOPSIGHT_DISPLAY_SIZE = 85;
const MAX_ARTICLES = 26;
const ARTICLE_FETCH_DEBOUNCE_MS = 500;

let initialized = false;
let articleFetchTimer = null;
let articleFetchSequence = 0;
let articleIconsCache = {};
let topSightIconsCache = {};

const getActiveLocationTopSights = (state) => {
  const active = state.activeTopicId ? state.topics[state.activeTopicId] : null;
  if (active && active.type === 'location') {
    return active.topSightsImages?.items ?? [];
  }

  const suggested = state.suggestedTopicId ? state.topics[state.suggestedTopicId] : null;
  if (suggested && suggested.type === 'location') {
    return suggested.topSightsImages?.items ?? [];
  }

  return [];
};

const toTopSightSignature = (items) => JSON.stringify(
  (items || []).map((item) => [
    String(item?.id || item?.link || ''),
    String(item?.thumbnail || ''),
  ])
);

async function ensureArticleIcons(articles) {
  const pending = (articles || []).filter(
    (article) => article?.thumbnail !== FALLBACK_THUMBNAIL
      && !articleIconsCache[article.pageid]
      && !articleIconsCache[`${article.pageid}_selected`]
  );

  if (pending.length === 0) return;

  try {
    const generated = await Promise.all(
      pending.map(async (article) => {
        const urlDefault = await iconWorkerManager.generateCircleIcon(article.thumbnail, article.pvSum);
        const urlSelected = await iconWorkerManager.generateCircleIcon(article.thumbnail, article.pvSum, '#32CD32', 1.3);
        return {
          [article.pageid]: urlDefault,
          [`${article.pageid}_selected`]: urlSelected,
        };
      })
    );

    const merged = Object.assign({}, ...generated);
    articleIconsCache = {
      ...articleIconsCache,
      ...merged,
    };

    patchAppState({
      articleIcons: {
        ...useAppStore.getState().articleIcons,
        ...merged,
      },
    });
  } catch (error) {
    console.error('[DiscoveryRuntime] Failed to generate article icons:', error);
  }
}

async function ensureTopSightIcons(items) {
  const pending = (items || []).filter(
    (item) => item?.id && item?.thumbnail
      && !topSightIconsCache[item.id]
      && !topSightIconsCache[`${item.id}_selected`]
  );

  if (pending.length === 0) return;

  try {
    const generated = await Promise.all(
      pending.map(async (item) => {
        const urlDefault = await iconWorkerManager.generateCircleIcon(item.thumbnail, 60000, null, 1, TOPSIGHT_DISPLAY_SIZE);
        const urlSelected = await iconWorkerManager.generateCircleIcon(item.thumbnail, 60000, '#32CD32', 1.3, TOPSIGHT_DISPLAY_SIZE);
        return {
          [item.id]: urlDefault,
          [`${item.id}_selected`]: urlSelected,
        };
      })
    );

    const merged = Object.assign({}, ...generated);
    topSightIconsCache = {
      ...topSightIconsCache,
      ...merged,
    };

    patchAppState({
      topSightIcons: {
        ...useAppStore.getState().topSightIcons,
        ...merged,
      },
    });
  } catch (error) {
    console.error('[DiscoveryRuntime] Failed to generate top sight icons:', error);
  }
}

function scheduleArticleFetch(state) {
  if (articleFetchTimer) {
    clearTimeout(articleFetchTimer);
    articleFetchTimer = null;
  }

  if (!state?.boundsSnapshot) return;

  const runId = ++articleFetchSequence;
  const { boundsSnapshot, viewportWikiLanguage, thumbResolution } = state;

  articleFetchTimer = window.setTimeout(async () => {
    try {
      const currentArticles = useAppStore.getState().articles;
      const diff = await wikiWorkerManager.fetchArticlesWithDiff(
        boundsSnapshot,
        viewportWikiLanguage,
        thumbResolution,
        currentArticles
      );

      if (runId !== articleFetchSequence) return;

      const finalArticles = [...diff.toKeep, ...diff.toAdd]
        .sort((a, b) => b.pvSum - a.pvSum)
        .slice(0, MAX_ARTICLES);

      const displayedArticles = new Set(useAppStore.getState().displayedArticles);
      finalArticles.forEach((article) => {
        if (article.thumbnail !== FALLBACK_THUMBNAIL) {
          displayedArticles.add(article.pageid);
        }
      });

      patchAppState({
        articles: finalArticles,
        displayedArticles,
      });

      void ensureArticleIcons(finalArticles);
    } catch (error) {
      console.error('[DiscoveryRuntime] Wikipedia fetch error:', error);
    }
  }, ARTICLE_FETCH_DEBOUNCE_MS);
}

function initAppStoreSubscription() {
  let prevBounds = useAppStore.getState().boundsSnapshot;
  let prevViewportWikiLanguage = useAppStore.getState().viewportWikiLanguage;
  let prevThumbResolution = useAppStore.getState().thumbResolution;
  let prevArticles = useAppStore.getState().articles;

  useAppStore.subscribe((state) => {
    const fetchInputsChanged = state.boundsSnapshot !== prevBounds
      || state.viewportWikiLanguage !== prevViewportWikiLanguage
      || state.thumbResolution !== prevThumbResolution;

    if (fetchInputsChanged) {
      prevBounds = state.boundsSnapshot;
      prevViewportWikiLanguage = state.viewportWikiLanguage;
      prevThumbResolution = state.thumbResolution;
      scheduleArticleFetch(state);
    }

    if (state.articles !== prevArticles) {
      prevArticles = state.articles;
      void ensureArticleIcons(state.articles);
    }
  });

  scheduleArticleFetch(useAppStore.getState());
}

function initTopicStoreSubscription() {
  let previousSignature = toTopSightSignature(getActiveLocationTopSights(useTopicStore.getState()));

  useTopicStore.subscribe((state) => {
    const currentItems = getActiveLocationTopSights(state);
    const currentSignature = toTopSightSignature(currentItems);
    if (currentSignature === previousSignature) return;
    previousSignature = currentSignature;

    const focusedTopSightMarkerId = useAppStore.getState().focusedTopSightMarkerId;
    if (focusedTopSightMarkerId) {
      const ids = currentItems
        .map((item) => String(item?.id || item?.link || '').trim())
        .filter(Boolean);
      if (!ids.includes(focusedTopSightMarkerId)) {
        setAppField('focusedTopSightMarkerId', null);
      }
    }

    void ensureTopSightIcons(currentItems);
  });

  void ensureTopSightIcons(getActiveLocationTopSights(useTopicStore.getState()));
}

export function ensureDiscoveryRuntime() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  articleIconsCache = { ...useAppStore.getState().articleIcons };
  topSightIconsCache = { ...useAppStore.getState().topSightIcons };

  initAppStoreSubscription();
  initTopicStoreSubscription();

  window.addEventListener('beforeunload', () => {
    if (articleFetchTimer) {
      clearTimeout(articleFetchTimer);
      articleFetchTimer = null;
    }
    iconWorkerManager.terminate();
    wikiWorkerManager.terminate();
  });
}
